import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import { Skeleton } from '../Skeleton';
import {
  hiringLinksAPI,
  type HiringCancelResult,
  type HiringLink,
  type HiringRaiseResult,
  type HiringRequest,
  type HiringTimelineEvent,
} from '../../services/api';
import { hiringMoveAPI, type HiringMoveResult } from '../../services/hiringRecruitmentApi';
import { CancelHiringStep } from './CancelHiringStep';
import { CancelStatus } from './CancelStatus';
import { HiringAttentionBox } from './HiringAttentionBox';
import { linkNeedsAPerson } from './linkAnswer';
import { HiringJoinerBox } from './HiringJoinerBox';
import { HiringRequestCard } from './HiringRequestCard';
import { ManageBudgetsLink } from './ManageBudgetsLink';
import { MoveStatus, MoveStoreStep } from './MoveStoreStep';
import { usePmLaunchStatus } from '../pm-launch/usePmLaunchStatus';
import { OpeningDeleted, OpeningNumbers, OpeningStatusPill, OpeningTabs, useOpeningDetails } from './OpeningView';
import { RaiseRequestStatus } from './RaiseRequestStatus';
import { StoreLeavingBox } from './StoreLeavingBox';
import { readTimeline } from './readTimeline';
import {
  CANCEL_CODES,
  CANCEL_WORDS,
  DELETED_WORDS,
  HIRING_CODES,
  MOVE_WORDS,
  NO_LONGER_FOLLOWED,
  OPENING_WORDS,
  PANEL_WORDS,
  RAISE_WORDS,
  REQUEST_WORDS,
  TIMELINE_WORDS,
  addressLines,
  attentionAsks,
  cancelErrorText,
  cancelLinkMoved,
  cancelOutcomeKind,
  closedHowOf,
  deletedInRecruitment,
  hasRaiseInFlight,
  hasRequestWaiting,
  formatHiringMoment,
  hiringCodeOf,
  hiringErrorText,
  isHiringOff,
  isStoppedFollowing,
  localiseRaiseMessage,
  moveCheckIsNews,
  movedLinkOf,
  moveOutcomeKind,
  openingStoreWords,
  openingTitle,
  peopleTimelineLine,
  raiseLinkMoved,
  raiseOutcomeKind,
  raisedOpeningFlag,
  timelineLineFor,
} from './hiringText';

/**
 * The hiring panel of one store or one opening: every opening it follows, each drawn as the
 * approved opening view (15 Oct 2026, OpeningView): one status pill, four numbers with one
 * (i), and Details · Team · Candidates. Opened on ONE opening (the Hiring dashboard's row)
 * the window itself is that opening: its title is "Designation · Opening N" with the pill
 * beside it, and the opening's own actions are its foot. A store with several openings (or
 * a waiting hiring request) keeps the panel's title and draws each opening as a card.
 *
 * Candidates' names and mobile numbers are shown to everyone who may see hiring (the product
 * owner's decision of 27 Oct 2026; until then to holders of "Raise & cancel hiring" only),
 * read live by the opening's own reads, and never kept. The window is Modal "xl" (64rem)
 * since 27 Oct 2026, so the Candidates table's seven columns are read without cuts ("lg",
 * 42rem, before).
 * The opening's history (its timeline) is one click away under Details, read only when
 * shown - or when the card must learn how a link someone else ended was closed.
 *
 * Read-only towards Recruitment. A person can stop following an opening, and that only
 * ends ChannelPlex's link — the opening is not touched. For a request to raise hiring
 * that Recruitment has not confirmed, the card shows where it stands, checks again (which
 * reads Recruitment's list and sends nothing), and records how it ended. Nothing here
 * can raise a request a second time.
 *
 * TWO THINGS HERE CAN CHANGE RECRUITMENT, and only for a person who holds "Raise & cancel
 * hiring" and whom the list answer says may cancel (`canCancel`): "Cancel hiring in Recruitment", beside "Stop following this
 * opening" on a link that is followed, is not shared and has an opening number. It asks
 * Recruitment to DELETE the opening, after a review and a typed reason (CancelHiringStep).
 * And, beside it on a store's own opening, "Move to another store" (15 Oct 2026): it asks
 * Recruitment to move the opening to another store of the project, after the new store is
 * picked and the server's review is read (MoveStoreStep), with one confirm. Its switch is the
 * same Cancel switch. A move Recruitment has not confirmed is shown with where it stands
 * (MoveStatus), checked by reading, and never sent again: no second move is offered, and no
 * cancel either, while it is open.
 * A cancel Recruitment has not confirmed is shown with where it stands (CancelStatus),
 * checked by reading Recruitment's list, and never sent again - also on the closed card of
 * a link that was stopped following while that cancel could not be settled, which is the
 * only place it can still be checked from. With `canCancel` false the foot of every card
 * is exactly what it was before Cancel existed.
 *
 * `links` is the owner's list as it is NOW (the Stores tab passes the store's live
 * links; the Hiring tab the one row that was opened or, once that row has left the list
 * on show, the link this panel was last answered with), so a re-read shows here without
 * the panel asking again. The exception is a link stopped from this panel: a list of
 * live links no longer holds it, so the panel keeps what the stop call returned and goes
 * on showing that card until it is closed. A link that turned out to have been stopped
 * or cancelled by someone else is kept the same way, with the sentence that says so: the
 * list behind the panel drops it on its re-read, and the explanation must not go with it.
 * That holds wherever the server said so (`LINK_NOT_LIVE`): to "Stop following", to the
 * cancel step, to an answer in the Needs attention box, and to the "Who joined" box.
 * Its pill says only "No longer followed" until the card's own timeline has said how it
 * ended: "Stopped following" would say the opening still exists in Recruitment, and an
 * opening someone cancelled does not.
 *
 * A CARD READS ITS WHOLE TIMELINE, every page of it, oldest first (`readTimeline`). The
 * line that says how a link ended is among the newest, and one page holds only the oldest.
 * Where a timeline is longer than the card reads, the card says so under it.
 *
 * A CARD IS NEVER TAKEN AWAY WHILE ITS CANCEL STEP HAS SOMETHING TO SAY. When the Cancel
 * step asks for the page's list to be read again - no answer came back from a confirm, a
 * review was refused because the list is out of date, Cancel was switched off - the panel
 * keeps that card itself, whatever the re-read brings: a list that no longer holds the
 * link (it was cancelled by the very request whose answer was lost; or the Hiring tab's
 * re-read did not reach its page) must not leave "No opening is linked" where "No answer
 * came back. Do not cancel it again." and "Ask again" stood. And when no answer came back
 * AND the re-read list no longer holds the link, the card reads what became of that cancel
 * once (the check, which never sends) and says it.
 *
 * A hiring request that is waiting for confirmation (one person asked for hiring to be
 * raised here, and nobody has confirmed or declined it) is drawn above the opening cards,
 * with a button that opens it. Nothing of it has been sent to Recruitment. The panel only
 * shows it: confirming, declining and withdrawing are the hiring request window's.
 *
 * OPENED TO DO ONE THING (`start`, 21 Oct 2026): the question asked when someone is deployed
 * (DeployHiringCheck; since 22 Oct 2026 before the deploy is saved) offers Move and Cancel for
 * each opening, and once the deploy is saved opens this window on that one opening with the
 * chosen step already open, as if its button had been pressed, under the owner's one line
 * (`notice`: "<person> is deployed. Now finish moving Opening N."): the cancel's review, or the
 * move's window once Move may be pressed (after the card's read of where a move stands);
 * "Stop following"'s question for a `stop` start. Each step keeps its own rules and
 * confirmations; where the card would not offer it, nothing opens by itself.
 *
 * DELETED IN RECRUITMENT (21 Oct 2026, the product owner: "Yes, please update that window").
 * 1Office's list of a budget's openings has no size limit and leaves an opening out only when
 * it, or its budget, was deleted; its delete also closes the opening's candidates. So an
 * opening that a good read of that list did not find - the link's status `not_listed`, or the
 * window's own read of the opening refused or answered "not listed" - was deleted there. Its
 * pill says "Deleted in Recruitment" (the colour of Needs attention); in place of its numbers,
 * its questions and its tabs, one plain notice (OpeningDeleted); and its foot offers only "Stop
 * following" (Hiring manage) and "Raise again" (a holder of "Raise & cancel hiring"; "Ask for
 * hiring" for a person who may only ask). No Cancel, no Move, no Manage Budgets: there is
 * nothing left of it in Recruitment. "Raise again" hands the owner this link, and the owner's
 * Raise window stops following it once that raise has made an opening (RaiseHiringModal
 * `replacingDeleted`). Nothing here keeps "deleted": a later read of the list that finds the
 * opening again (1Office restored it) puts the card back as it was.
 *
 * Messages from Recruitment. When one of them left a question on a link (the address
 * changed, the positions changed, the opening was deleted or closed there), the card asks
 * it in a box of its own (HiringAttentionBox), in place of the plain "changed in
 * Recruitment" box the live read alone would draw. Nothing of an answer is sent to
 * Recruitment. A card also says the expected joining date of an offer, and who joined, by
 * employee code, when Recruitment said so.
 */

const NO_REQUESTS: readonly HiringRequest[] = [];

/**
 * What "Raise again" (or "Ask for hiring") on a deleted opening gets back from the owner: nothing
 * when its window opened at once; a promise when the owner reads something first, which ends
 * with the sentence to say here when the window could not be opened (null when it was).
 */
export type ReplacingOpen = void | Promise<string | null | undefined>;

/** One opening's step to open at once (the question asked when someone is deployed): its link, and which step. */
export interface HiringPanelStart { linkId: string; step: 'move' | 'cancel' | 'stop' }

export type HiringPanelSubject =
  // `storeId`: the store itself, where the owner knows it. With it the panel also says who is leaving the store.
  | { kind: 'store'; storeName: string; storeCode: string; storeId?: string }
  | { kind: 'role'; roleLabel: string };

const TIMELINE_DOT: Record<string, string> = {
  linked: 'bg-emerald-500',
  released: 'bg-gray-400',
  // A raise: asked, created, refused, not confirmed, and how a person or the list settled it.
  send_claimed: 'bg-violet-500',
  send_confirmed: 'bg-emerald-500',
  send_settled: 'bg-emerald-500',
  send_refused: 'bg-red-400',
  send_not_sent: 'bg-gray-400',
  send_unknown: 'bg-amber-500',
  attention_answered: 'bg-emerald-500',
  // The opening was raised from a hiring request: who asked. The entry's own actor is who confirmed.
  request_confirmed: 'bg-violet-500',
  // What Recruitment said happened there: candidates, an offer, a joining; then what needs a person.
  recruitment_counts_changed: 'bg-sky-400',
  recruitment_offer_made: 'bg-sky-500',
  recruitment_offer_withdrawn: 'bg-gray-400',
  recruitment_not_joined: 'bg-gray-400',
  recruitment_joined: 'bg-emerald-500',
  recruitment_reopened: 'bg-emerald-500',
  recruitment_address_changed: 'bg-amber-500',
  recruitment_positions_changed: 'bg-amber-500',
  recruitment_deleted: 'bg-red-400',
  recruitment_closed_on_exit: 'bg-red-400',
  recruitment_not_listed: 'bg-red-400',
  // A cancel: asked, deleted, refused, not confirmed, not sent, and found still listed.
  cancel_claimed: 'bg-violet-500',
  cancelled: 'bg-gray-700',
  cancel_refused: 'bg-red-400',
  cancel_unknown: 'bg-amber-500',
  cancel_not_sent: 'bg-gray-400',
  cancel_not_applied: 'bg-gray-400',
};

const FLAG_LINE = 'rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900';

/**
 * A link someone else ended first, as this panel can still show it: no longer followed,
 * with the figures it had. HOW it ended is not known here - the server only said it is no
 * longer live: someone stopped following it (nothing changed in Recruitment), someone
 * cancelled it (Recruitment deleted the opening), someone closed it because Recruitment
 * had deleted or closed the opening, or its joiner was placed and it was closed as filled.
 * So it is called none of these; the card's timeline, read again, says which, by whom and
 * when (`closedHowOf` in hiringText).
 */
const asEndedElsewhere = (link: HiringLink): HiringLink => ({
  ...link,
  state: 'closed',
  closedAs: null,
  closedBy: null,
  closedAt: null,
  status: 'no_hiring',
  statusLabel: NO_LONGER_FOLLOWED,
  numbersFrom: link.numbers ? 'last_seen' : null,
  stages: link.numbers ? link.stages : null,
  stagesFrom: link.numbers && link.stages ? 'last_seen' : null,
  lastKnownStatus: null,
  unavailableReason: null,
  flags: { addressChanged: false, positionsChanged: false, joinerExiting: false, candidatesUnknown: false },
  cancel: null,
});

/** A card this panel only knows to be "no longer followed": how it ended has not been read yet. */
const endedHowUnknown = (link: HiringLink): boolean =>
  link.state === 'closed' && link.closedAs == null && link.statusLabel === NO_LONGER_FOLLOWED;

/** "Stop following": a quiet button in the opening's foot. */
const STOP_BUTTON = 'inline-flex h-9 items-center rounded-lg px-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100';
/**
 * The owner's one line at the top of the window (`notice`): above the scrolling body, so the step
 * the window opens at, which scrolls itself into view there, cannot push it out of sight.
 */
const NOTICE_BOX = 'px-6 pb-2 pt-4';
const NOTICE = 'rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900';
const KEEP_BUTTON = 'rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50';
const STOP_YES = 'inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50';
/** "Raise again" (or "Ask for hiring") at a deleted opening's foot: the filled one, as the panel's Raise hiring. */
const RAISE_AGAIN = 'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-600 px-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50';

function HiringLinkCard({
  projectId, link, listed, canManage, canCancel, onReleased, onKeep, onChanged, onHiringGone, onRaiseAgain, single = false, extra, startWith,
  onRaiseReplacing, onAskReplacing, onReadDeleted,
}: {
  projectId: number;
  link: HiringLink;
  /** The step this card opens by itself, once, where it would offer it (the panel's `start`). */
  startWith?: HiringPanelStart['step'];
  /** The window shows this opening alone: its title and pill are the window's, and its foot is the window's foot. */
  single?: boolean;
  /** Drawn at the end of the opening's Details (a store's people leaving). */
  extra?: ReactNode;
  /** The owner's list holds this link now. False once a re-read no longer brings it (the card is then one this panel kept). */
  listed: boolean;
  canManage: boolean;
  /** The list answer says this person may cancel openings in Recruitment for this project. */
  canCancel: boolean;
  onReleased: (closed: HiringLink) => void;
  /** This card must stay on show whatever the owner's re-read brings: its cancel step has something to say. */
  onKeep: (link: HiringLink) => void;
  onChanged: (stopped?: HiringLink) => void;
  onHiringGone: () => void;
  /** Opens Raise hiring for this store or role once its link has been closed for it; only where the owner can. */
  onRaiseAgain?: () => void;
  /**
   * "Raise again" on an opening deleted in Recruitment: opens Raise hiring with this link, which
   * that raise stops following once it has made an opening. A promise, where the owner reads
   * something first: its sentence is said here when the window could not be opened.
   */
  onRaiseReplacing?: () => ReplacingOpen;
  /** "Ask for hiring" on an opening deleted in Recruitment, for a person who may only ask. Lets go of nothing. */
  onAskReplacing?: () => ReplacingOpen;
  /** The opening's own read found it deleted, or no longer does: for the window's pill. */
  onReadDeleted?: (deleted: boolean) => void;
}) {
  const [events, setEvents] = useState<HiringTimelineEvent[]>([]);
  // The timeline is longer than the card reads: the lines on show are not all of it.
  const [timelineCut, setTimelineCut] = useState(false);
  const [timelineState, setTimelineState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [timelineReads, setTimelineReads] = useState(0);
  // Which read of the timeline the events on show came from, and the read that was the
  // latest when this card learned its link had been ended by someone else: only a read
  // made AFTER that moment may say how it ended.
  const readSeq = useRef(0);
  const [eventsSeq, setEventsSeq] = useState(0);
  const endedAfterSeq = useRef(0);
  // Opened to stop following: the question is asked at once, as "Stop following" asks it.
  const [confirming, setConfirming] = useState(startWith === 'stop');
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // How a request to raise hiring ended, said once it has: the server's own sentence.
  const [raiseEnded, setRaiseEnded] = useState<string | null>(null);
  // A cancel of this opening: the answer that left it open (until the list says so itself),
  // how the last one ended, and that Cancel was switched off while this card was on show.
  const [cancelWaiting, setCancelWaiting] = useState<Pick<HiringCancelResult, 'outcome' | 'message'> | null>(null);
  const [cancelEnded, setCancelEnded] = useState<string | null>(null);
  const [cancelGone, setCancelGone] = useState(false);
  // The cancel an answer here has ended: the card stops saying it is open at once, whether
  // or not the owner's list has caught up yet.
  const [cancelEndedSend, setCancelEndedSend] = useState<string | null>(null);
  // No answer came back from a confirm of a cancel on this card: what became of it is not
  // known here. When the owner's re-read list no longer holds the link, it is read once.
  const [lostCancel, setLostCancel] = useState(false);
  const [readingLost, setReadingLost] = useState(false);
  const readLost = useRef(false);
  // The link as the owner was last told a cancel left it: it is told again only when that moved.
  const toldAfterCancel = useRef<HiringLink | null>(null);
  // The question just closed without a stop: its button takes keyboard focus back.
  const refocusStop = useRef(false);
  // "Move to another store": its window is open in the foot; a move Recruitment has not
  // confirmed (the list does not say so: an answer here does, or the card's own read of
  // where a move stands when it is first shown); how the last one ended; and the store this
  // card's link now follows, until the owner's list says so itself.
  const [moving, setMoving] = useState(false);
  const [moveWaiting, setMoveWaiting] = useState<{ message: string } | null>(null);
  const [moveEnded, setMoveEnded] = useState<string | null>(null);
  const [movedTo, setMovedTo] = useState<{ fromId: string; subject: HiringLink['subject'] } | null>(null);
  // The card's read of where a move of this opening stands is not back yet: Move waits for it.
  const [moveReading, setMoveReading] = useState(false);
  const moveReadFor = useRef<string | null>(null);
  // That read has come back (or failed): opened to move, the move's window may open now.
  const [moveReadSettled, setMoveReadSettled] = useState(false);
  // Opened to cancel or to move (`startWith`): each opens once, or never.
  const startCancel = useRef(startWith === 'cancel');
  const startMove = useRef(startWith === 'move');
  // The store the card was first shown for: what the owner drew under its Details (who is leaving) is that store's.
  const firstStoreId = useRef(link.subject.kind === 'store' ? link.subject.storeId ?? null : null);
  const refocusMove = useRef(false);
  // Where a move's answer is said: brought into view when it arrives (the window had scrolled down to the review).
  const moveSaid = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = moveSaid.current;
    if ((moveWaiting || moveEnded) && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [moveWaiting, moveEnded]);
  const alive = useRef(true);
  // The owner's callbacks as they are now, without making the timeline re-read on each render.
  const gone = useRef(onHiringGone);
  gone.current = onHiringGone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The history is read when it is shown, and when the card must learn from it how a link
  // someone else ended was closed. Nothing else asks for it.
  const [historyShown, setHistoryShown] = useState(false);
  const wantTimeline = historyShown || endedHowUnknown(link);
  useEffect(() => {
    if (!wantTimeline) return undefined;
    // An answer for a card that has since closed, or been asked again, is dropped - and
    // that reading asks for no further page.
    let cancelled = false;
    readSeq.current += 1;
    const seq = readSeq.current;
    setTimelineState('loading');
    Promise.resolve()
      // Every page, oldest first: the line that says how a link ended is among the newest.
      .then(() => readTimeline(projectId, link.id, () => cancelled))
      .then((read) => {
        if (cancelled || !read) return;
        setEvents(read.events);
        setTimelineCut(read.cut);
        setEventsSeq(seq);
        setTimelineState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isHiringOff(err)) gone.current();
        else setTimelineState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, link.id, timelineReads, wantTimeline]);

  // A card that only knows it is "no longer followed" learns how it ended from its own
  // timeline, once that has been read again: stopped following, or cancelled - by whom, when.
  const tellReleased = useRef(onReleased);
  tellReleased.current = onReleased;
  useEffect(() => {
    // Not from the timeline as it was read while the link was still followed.
    if (timelineState !== 'ready' || !endedHowUnknown(link) || eventsSeq <= endedAfterSeq.current) return;
    const how = closedHowOf(events);
    if (how) tellReleased.current({ ...link, ...how });
  }, [timelineState, events, eventsSeq, link]);
  /** The link was ended by someone else: the card says so, and reads its timeline again to learn how. */
  const endedElsewhereNow = () => {
    endedAfterSeq.current = readSeq.current;
    onReleased(asEndedElsewhere(link));
    setTimelineReads((n) => n + 1);
  };
  /**
   * The server said this link is no longer followed, to the cancel step or to one of this
   * card's boxes (an answer in Needs attention, the "Who joined" list, a placing): someone
   * else stopped, cancelled or closed it first.
   */
  const linkGone = (sentence: string) => {
    // The list on the page is out of date too.
    onChanged();
    if (!alive.current) return;
    // The owner's re-read drops this link. The card stays, as no longer followed, so the
    // sentence is still there to read; its timeline says how it ended, and who.
    endedElsewhereNow();
    setError(sentence);
  };

  const stop = async () => {
    setStopping(true);
    setError(null);
    try {
      const res = await hiringLinksAPI.release(projectId, link.id);
      const closed = res?.data?.data;
      // The owner is told whether or not this panel is still open.
      onChanged(closed);
      if (!alive.current) return;
      if (closed) onReleased(closed);
      // A cancel had been sent for this opening, and the server read Recruitment's list
      // before it let go: the opening was gone. The link came back CANCELLED, and the card
      // says why "Stop following" ended that way.
      if (closed?.closedAs === 'cancelled') setCancelEnded(CANCEL_WORDS.stopFoundCancelled(closed.openingId ?? link.openingId));
      // What an answer here said of a cancel was said of a link that was still followed.
      setCancelWaiting(null);
      setConfirming(false);
      setTimelineReads((n) => n + 1);
    } catch (err) {
      if (isHiringOff(err)) {
        onHiringGone();
        return;
      }
      const code = hiringCodeOf(err);
      // Someone else stopped or cancelled it first: the list on the page is out of date too.
      const endedElsewhere = code === HIRING_CODES.linkNotLive;
      // A cancel of this opening is open that this page does not show yet (a colleague
      // sent it after the page was read). The list is read again - and the cancel's box is
      // drawn at once, so the "Check again" the server's sentence asks for is there to press.
      const cancelIsOpenElsewhere = code === CANCEL_CODES.cancelInProgress;
      if (cancelIsOpenElsewhere) onKeep(link);
      if (endedElsewhere || cancelIsOpenElsewhere) onChanged();
      if (!alive.current) return;
      // The owner's re-read drops this link. The card stays, as no longer followed, so the
      // sentence below is still there to read; its timeline says how it ended, and who.
      if (endedElsewhere) endedElsewhereNow();
      if (cancelIsOpenElsewhere) setCancelWaiting((was) => was ?? { outcome: 'not_confirmed', message: '' });
      setError(hiringErrorText(err, 'Could not stop following this opening. Try again.'));
      refocusStop.current = true;
      setConfirming(false);
    } finally {
      if (alive.current) setStopping(false);
    }
  };

  // Deleted in Recruitment, as the link's status says it. Nothing of such an opening is read.
  const goneByStatus = deletedInRecruitment(link);
  // The opening's details, read once it is shown (only a followed link with an opening number),
  // and again after a move here: its store and address are the new store's.
  const details = useOpeningDetails(projectId, link, onHiringGone, goneByStatus);
  // Or as the opening's own reads say it: its details found it not listed, or its team or its
  // candidates were refused for that. Never of a raised opening the list has not shown yet: that
  // one has its own question, and Recruitment's read copy may be behind. Nor of one raised a
  // moment ago that the list did not find (`not_listed_yet`): the list holds back for two
  // minutes there, and these reads have no such wait.
  const [tabsSaidGone, setTabsSaidGone] = useState(false);
  const goneByRead = link.state === 'live' && !(link.attention ?? []).includes('not_listed')
    && !(link.status === 'numbers_unavailable' && link.unavailableReason === 'not_listed_yet')
    && (details.data?.listed === false || tabsSaidGone);
  const deleted = goneByStatus || goneByRead;
  // What the opening's own reads said is kept no longer than the read of the list it was said
  // beside: a newer one (1Office may have restored the opening) asks them again.
  const listRead = `${link.seenAt ?? ''}|${link.status}`;
  const listReadWas = useRef(listRead);
  const readGoneNow = useRef(goneByRead);
  readGoneNow.current = goneByRead;
  const retryDetails = useRef(details.retry);
  retryDetails.current = details.retry;
  useEffect(() => {
    if (listReadWas.current === listRead) return;
    listReadWas.current = listRead;
    if (!readGoneNow.current) return;
    setTabsSaidGone(false);
    retryDetails.current();
  }, [listRead]);
  const tellReadDeleted = useRef(onReadDeleted);
  tellReadDeleted.current = onReadDeleted;
  useEffect(() => {
    tellReadDeleted.current?.(goneByRead);
  }, [goneByRead]);
  // "Raise again" (or "Ask for hiring") is being opened: the owner reads the store first.
  const [openingRaise, setOpeningRaise] = useState(false);
  const openReplacing = (open: () => ReplacingOpen) => {
    setError(null);
    const out = open() as Promise<string | null | undefined> | undefined;
    if (!out) return;
    setOpeningRaise(true);
    out
      .then((sentence) => {
        if (sentence && alive.current) setError(sentence);
      })
      .catch((err: unknown) => {
        if (isHiringOff(err)) {
          onHiringGone();
          return;
        }
        if (alive.current) setError(hiringErrorText(err, DELETED_WORDS.openFailed));
      })
      .finally(() => {
        if (alive.current) setOpeningRaise(false);
      });
  };

  // A check, or a person's choice, was answered for a request that is being raised.
  const raiseAnswered = (result: HiringRaiseResult) => {
    // The owner is told whether or not this panel is still open, and only when the
    // request moved on: a check that found nothing new changes nothing on its list.
    if (raiseLinkMoved(link, result.link)) onChanged(result.link);
    if (!alive.current) return;
    // A request that closed leaves a list of live links: the panel keeps its card.
    if (result.link.state === 'closed') onReleased(result.link);
    if (raiseOutcomeKind(result.outcome) !== 'waiting') setRaiseEnded(localiseRaiseMessage(result.message));
    setTimelineReads((n) => n + 1);
  };

  // A cancel, or a check of one, was answered.
  const cancelAnswered = (result: HiringCancelResult) => {
    const kind = cancelOutcomeKind(result.outcome);
    // Cancelled: the link as the server closed it. Anything else leaves it followed: the
    // link as this page has it (its figures were read; the answer's were not), with what
    // the answer changed - its open questions, and where its cancel stands.
    const after: HiringLink = kind === 'cancelled' || result.link.state === 'closed'
      ? result.link
      // Whether it still needs a person is said of the questions it has NOW: the page's own word is from before this answer.
      : { ...link, attention: result.link.attention ?? link.attention, cancel: result.link.cancel ?? null, needsAttention: linkNeedsAPerson({ state: link.state, attention: result.link.attention ?? link.attention }) };
    // The owner is told whether or not this panel is still open, and only when something
    // moved - against its own list, and against what it was last told from here.
    if (cancelLinkMoved(link, after) && (!toldAfterCancel.current || cancelLinkMoved(toldAfterCancel.current, after))) {
      toldAfterCancel.current = after;
      onChanged(after);
    }
    if (!alive.current) return;
    // A cancelled link leaves a list of live links: the panel keeps its card.
    if (after.state === 'closed') onReleased(after);
    if (kind === 'waiting') {
      setCancelWaiting({ outcome: result.outcome, message: result.message });
      setCancelEnded(null);
      setCancelEndedSend(null);
    } else {
      setCancelWaiting(null);
      setCancelEnded(localiseRaiseMessage(result.message));
      setCancelEndedSend(result.sendId);
    }
    // Whatever was not known about a confirm of this cancel is known now.
    setLostCancel(false);
    setError(null);
    setTimelineReads((n) => n + 1);
  };
  // A move, or a check of one, was answered.
  const moveAnswered = (result: HiringMoveResult) => {
    const kind = moveOutcomeKind(result.outcome);
    const after = kind === 'moved' ? movedLinkOf(link, result.link) : null;
    if (after) {
      // The card is kept BEFORE the owner reads again: a store's list no longer holds a link
      // that now follows another store, and the sentence of the move must not go with it.
      onKeep(after);
      // The owner is told whether or not this panel is still open.
      onChanged(after);
    }
    if (!alive.current) return;
    if (after) {
      const fromId = link.subject.kind === 'store' ? link.subject.storeId : null;
      if (fromId && after.subject.kind === 'store' && after.subject.storeId && after.subject.storeId !== fromId) {
        setMovedTo({ fromId, subject: after.subject });
      }
      // The Details tab named the old store and its address: read again, it names the new one.
      details.retry();
    }
    if (kind === 'waiting') {
      setMoveWaiting({ message: result.message });
      setMoveEnded(null);
    } else {
      setMoveWaiting(null);
      setMoveEnded(localiseRaiseMessage(result.message));
    }
    setError(null);
    setTimelineReads((n) => n + 1);
  };

  const cancelAnsweredNow = useRef(cancelAnswered);
  cancelAnsweredNow.current = cancelAnswered;
  const moveAnsweredNow = useRef(moveAnswered);
  moveAnsweredNow.current = moveAnswered;

  // No answer came back from "Yes, cancel this opening", and the owner's list - read again -
  // no longer holds this link. It may be this very cancel that closed it: Recruitment
  // deleted the opening and its candidates were told. What became of it is read ONCE, with
  // the check - which reads the ledger and Recruitment's list and never sends anything -
  // and its sentence is said here. (Repeating the confirm by itself could be the first
  // request to arrive: a click is needed for that, and "Ask again" stays for it.)
  useEffect(() => {
    if (!lostCancel || listed || readLost.current || link.state !== 'live') return;
    readLost.current = true;
    setReadingLost(true);
    Promise.resolve()
      .then(() => hiringLinksAPI.checkCancel(projectId, link.id))
      .then((res) => {
        const result = res?.data?.data;
        if (result) cancelAnsweredNow.current(result);
      })
      .catch((err: unknown) => {
        if (isHiringOff(err)) {
          gone.current();
          return;
        }
        if (!alive.current) return;
        // No cancel is on record for this opening: that confirm never claimed anything.
        if (hiringCodeOf(err) === CANCEL_CODES.linkNotCancelling) setError(CANCEL_WORDS.lostNothingSent);
        else if (hiringCodeOf(err)) setError(cancelErrorText(err, CANCEL_WORDS.checkFailed));
        // No answer to the check either: "Ask again" is still there to press.
      })
      .finally(() => {
        if (alive.current) setReadingLost(false);
      });
  }, [lostCancel, listed, link.state, link.id, projectId]);

  const sending = link.state === 'sending';
  const opening = openingTitle(link);
  const address = addressLines(link);
  const live = link.state === 'live';
  const raisedFlag = raisedOpeningFlag(link);
  // A cancel of this opening is open: the list says so (of a cancel no answer here has
  // ended), or the answer just given did. Also on a link that was stopped following while
  // its cancel could not be settled: the list still says so, and its card can still check it.
  const cancelOpen = (live || isStoppedFollowing(link))
    && ((link.cancel != null && link.cancel.sendId !== cancelEndedSend) || (live && cancelWaiting != null));
  // The store this link follows now: the owner's list, or - just after a move here, until
  // that list has been read again - the store it was moved to.
  const subjectNow = movedTo && link.subject.kind === 'store' && link.subject.storeId === movedTo.fromId ? movedTo.subject : link.subject;
  // A move of this opening that Recruitment has not confirmed: no second move, and no cancel.
  const moveOpen = live && moveWaiting != null;
  // Cancel is offered on a link that is followed, is not shared, has an opening number and has no cancel (or move) open.
  // Nothing is left of a deleted opening in Recruitment to cancel or to move.
  const mayCancelHere = live && canManage && canCancel && !cancelGone && !deleted;
  const cancelOffered = mayCancelHere && !link.shared && link.openingId != null && !cancelOpen && !moveOpen;
  // "Move to another store": the same switch and right, on a store's own opening only.
  const moveFrom = subjectNow.kind === 'store' && subjectNow.storeId
    ? { storeId: subjectNow.storeId, storeName: subjectNow.storeName, storeCode: subjectNow.storeCode }
    : null;
  const moveOffered = cancelOffered && moveFrom != null;
  // The move's window is drawn (and the foot laid out for it) only where it may be.
  const moveShown = moving && mayCancelHere && moveFrom != null;
  // What the owner drew at the end of the Details (who is leaving the store) is the store's the
  // card was first shown for. Once the opening follows another store - a move here, or the
  // owner's list read again after one - it is that store's.
  const storeIdNow = subjectNow.kind === 'store' ? subjectNow.storeId ?? null : null;
  const extraNow = extra && firstStoreId.current && storeIdNow && storeIdNow.toLowerCase() !== firstStoreId.current.toLowerCase()
    ? <StoreLeavingBox key={storeIdNow} projectId={projectId} storeId={storeIdNow} onHiringGone={onHiringGone} />
    : extra;
  // The list says nothing of a move that is open (only of a cancel). So where Move would be
  // offered, the card reads ONCE, when it is first shown, where a move of this opening stands
  // (the check: it reads, and never sends). A move still open is drawn as if just answered -
  // "Recruitment did not confirm" and "Check again", no second move and no cancel - also in a
  // window opened afresh, or after a 202 that came back once the window had closed. A move the
  // read finds done is shown as moved: the server's check, finding the opening at the new store
  // in Recruitment, has moved the link and written its timeline - so it is said, the owner is
  // told and the Details are read again, as if just answered. The ending of an earlier move is
  // over: its sentence is not said again. Until the read is back,
  // "Move to another store" cannot be pressed; Cancel can, and the server refuses it while a
  // move is open (CANCEL_IN_PROGRESS).
  // (Not while a cancel is open: one send of a link is open at a time.)
  const moveReadWanted = mayCancelHere && !link.shared && link.openingId != null && !cancelOpen
    && link.subject.kind === 'store' && !!link.subject.storeId;
  // The store the card shows when it reads: a move that took the opening elsewhere is news.
  const moveReadFrom = link.subject.kind === 'store' ? link.subject.storeId ?? null : null;
  useEffect(() => {
    if (!moveReadWanted || moveReadFor.current === link.id) return;
    moveReadFor.current = link.id;
    setMoveReading(true);
    Promise.resolve()
      .then(() => hiringMoveAPI.check(projectId, link.id))
      .then((result) => {
        if (!result || typeof result.outcome !== 'string') return;
        if (moveOutcomeKind(result.outcome) === 'waiting') {
          if (alive.current) setMoveWaiting((was) => was ?? { message: result.message });
        } else if (moveCheckIsNews(result, moveReadFrom)) {
          // The owner is told whether or not this panel is still open (moveAnswered checks).
          moveAnsweredNow.current(result);
        }
      })
      .catch((err: unknown) => {
        // LINK_NOT_MOVING: no move on record. Any other failure offers Move: its review is
        // refused while something sent is open (SEND_IN_PROGRESS), with "Check again".
        if (isHiringOff(err)) gone.current();
      })
      .finally(() => {
        if (!alive.current) return;
        setMoveReading(false);
        setMoveReadSettled(true);
      });
  }, [moveReadWanted, moveReadFrom, link.id, projectId]);
  // Opened to move: the move's window opens by itself once "Move to another store" could be
  // pressed - after the read above, and only if the card then offers Move (no move or cancel
  // open). Where Move is not offered at all, nothing opens.
  useEffect(() => {
    if (!startMove.current) return;
    if (!moveReadWanted) {
      startMove.current = false;
      return;
    }
    if (!moveReadSettled) return;
    startMove.current = false;
    if (moveOffered) setMoving(true);
  }, [moveReadWanted, moveReadSettled, moveOffered]);
  // Opened to cancel: only by the first render's foot. A cancel step drawn later (after a
  // question was closed) starts as it always did, with its button.
  const cancelStartsNow = startCancel.current && cancelOffered;
  useEffect(() => {
    startCancel.current = false;
  }, []);
  /** "Move to another store": a quiet button between "Cancel opening" and "Stop following". */
  const moveButton = (disabled: boolean) => (
    <button
      type="button"
      ref={(el) => {
        if (el && refocusMove.current) {
          el.focus();
          if (document.activeElement === el) refocusMove.current = false;
        }
      }}
      onClick={() => {
        setError(null);
        setMoveEnded(null);
        setMoving(true);
      }}
      disabled={disabled || moveReading}
      title={MOVE_WORDS.buttonExplained}
      // One line whatever the width: the foot's row wraps between buttons, never inside one.
      className="inline-flex h-9 items-center whitespace-nowrap rounded-lg px-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:opacity-50"
    >
      {MOVE_WORDS.button}
    </button>
  );
  /** "Stop following this opening": the same button with or without Cancel beside it. */
  const stopButton = (disabled: boolean) => (
    <button
      type="button"
      ref={(el) => {
        if (el && refocusStop.current) {
          el.focus();
          if (document.activeElement === el) refocusStop.current = false;
        }
      }}
      onClick={() => {
        setError(null);
        setConfirming(true);
      }}
      disabled={disabled}
      // Said only to a person who may cancel, on hover: why this shared opening has no Cancel beside it.
      title={mayCancelHere && link.shared ? CANCEL_WORDS.sharedLine : undefined}
      className={disabled ? `${STOP_BUTTON} opacity-50` : STOP_BUTTON}
    >
      {OPENING_WORDS.stop}
    </button>
  );

  const timeline = (
    <div className="mt-3">
      <h5 className="sr-only">{OPENING_WORDS.historyLabel}</h5>
      {timelineState === 'loading' ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-4 w-28 shrink-0" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      ) : timelineState === 'failed' ? (
        <p className="text-sm text-gray-500">Could not load the history.</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing recorded yet.</p>
      ) : (
        <ol aria-label={OPENING_WORDS.historyLabel} className="max-h-56 space-y-0 overflow-y-auto pr-1">
          {events.map((ev, i) => {
            const line = peopleTimelineLine(ev) ?? timelineLineFor(ev, link.subject.kind);
            return (
              <li key={ev.id} className="flex gap-3">
                <div className="w-32 shrink-0 pt-0.5 text-right text-xs text-gray-500">{formatHiringMoment(ev.occurredAt)}</div>
                <div className="flex flex-col items-center">
                  <div className={`z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full ${TIMELINE_DOT[ev.type] || 'bg-gray-300'}`} />
                  {i < events.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                </div>
                <div className="flex-1 pb-3">
                  <p className="text-[13px] text-gray-900">{line.title}</p>
                  {line.details.map((detail) => (
                    <p key={detail} className="text-xs text-gray-600">{detail}</p>
                  ))}
                  {line.note && <p className="mt-0.5 text-[13px] italic text-gray-600">“{line.note}”</p>}
                  {ev.actor && <p className="text-xs text-gray-500">{ev.actor}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {/* Never a silent stop: the card read as many pages as it reads, and there are more. */}
      {timelineState === 'ready' && timelineCut && (
        <p className="mt-2 text-xs text-amber-800">{TIMELINE_WORDS.notAll(events.length)}</p>
      )}
    </div>
  );

  // A deleted opening's foot: "Stop following" (Hiring manage) and "Raise again" - or, for a
  // person who may only ask, "Ask for hiring". A view-only person has neither.
  const noStore = link.subject.kind !== 'store';
  // Not while a cancel of it is open: nothing can be let go of until that cancel settles
  // (CANCEL_IN_PROGRESS), and a settled cancel closes the link by itself.
  const replaceShown = live && deleted && !cancelOpen;
  const raiseHere = replaceShown ? onRaiseReplacing : undefined;
  const askHere = replaceShown && !raiseHere ? onAskReplacing : undefined;
  const deletedFoot = live && deleted && !confirming && (canManage || raiseHere || askHere) ? (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {canManage ? stopButton(openingRaise) : <span />}
      {raiseHere && (
        <button
          type="button"
          onClick={() => openReplacing(raiseHere)}
          disabled={openingRaise}
          title={noStore ? DELETED_WORDS.raiseAgainHoverNoStore : DELETED_WORDS.raiseAgainHover}
          className={RAISE_AGAIN}
        >
          {openingRaise && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {DELETED_WORDS.raiseAgain}
        </button>
      )}
      {askHere && (
        <button
          type="button"
          onClick={() => openReplacing(askHere)}
          disabled={openingRaise}
          title={noStore ? DELETED_WORDS.askHoverNoStore : DELETED_WORDS.askHover}
          className={RAISE_AGAIN}
        >
          {openingRaise && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {DELETED_WORDS.ask}
        </button>
      )}
    </div>
  ) : null;

  // The foot: "Cancel opening" and "Stop following" (each where this person may), and the Manage Budgets page.
  const stopArea = deletedFoot ?? (live && canManage ? (
    confirming ? (
      // Two clicks, as Remove on a store's team: the first only asks.
      <div
        role="group"
        aria-label={`Confirm stop following ${link.openingId != null ? `opening ${link.openingId}` : 'this request'}`}
        className="flex flex-wrap items-center gap-2"
      >
        <p className="text-[13px]">
          <span className="font-medium text-gray-800">{OPENING_WORDS.stopQuestion}</span>{' '}
          <span className="text-gray-500">{OPENING_WORDS.stopNote}</span>
        </p>
        <div className="flex items-center gap-2">
          {/* Focus lands on the safe answer: the button that was clicked has just left the page. */}
          <button
            type="button"
            autoFocus
            onClick={() => {
              refocusStop.current = true;
              setConfirming(false);
            }}
            disabled={stopping}
            className={KEEP_BUTTON}
          >
            {OPENING_WORDS.keep}
          </button>
          <button type="button" onClick={() => void stop()} disabled={stopping} className={STOP_YES}>
            {stopping && <Loader2 className="h-3 w-3 animate-spin" />}
            {OPENING_WORDS.stopYes}
          </button>
        </div>
      </div>
    ) : moveShown && moveFrom ? (
      <MoveStoreStep
        projectId={projectId}
        link={link}
        from={moveFrom}
        // A cancel open on this card has its own box and its own "Check again".
        cancelOpen={cancelOpen}
        onResult={moveAnswered}
        onDone={() => {
          refocusMove.current = true;
          setMoving(false);
        }}
        onMoveGone={(lost) => {
          // The project's Cancel switch (which is Move's too) was turned off, or stopped on the
          // server, since the page asked: both controls go, and the owner reads again.
          onKeep(link);
          onChanged();
          if (!alive.current) return;
          setCancelGone(true);
          setMoving(false);
          // It was "Ask again" of a confirm whose answer never came back: the move may have
          // gone out. Its warning stays, with "Check again" (the check needs neither switch).
          if (lost) {
            setMoveWaiting({ message: MOVE_WORDS.lostSwitchedOff });
            setMoveEnded(null);
          }
        }}
        onHiringGone={onHiringGone}
        onListStale={() => {
          // Kept BEFORE the owner reads again: the window's sentence - a refusal, or "No answer
          // came back" with "Ask again" - must not go with a row the re-read no longer brings.
          // (After a lost answer nothing is read by itself: "Ask again" repeats the very same
          // request, which the server answers from its first send.)
          onKeep(link);
          onChanged();
        }}
        onLinkGone={(sentence) => {
          setMoving(false);
          linkGone(sentence);
        }}
      />
    ) : cancelOffered ? (
      <CancelHiringStep
        projectId={projectId}
        link={link}
        stopButton={stopButton}
        besideCancel={moveOffered ? moveButton : undefined}
        startReview={cancelStartsNow}
        onResult={cancelAnswered}
        onCancelGone={() => {
          // Switched off for the project, or stopped on the server, since the page asked:
          // the control goes, and the owner reads again what this person may do. The
          // card stays, whatever that re-read brings.
          onKeep(link);
          onChanged();
          if (alive.current) setCancelGone(true);
        }}
        onHiringGone={onHiringGone}
        onListStale={(why) => {
          // The card is kept BEFORE the owner reads again: the step's sentence - a
          // refusal, or "No answer came back" with "Ask again" - must not go with a row
          // the re-read no longer brings.
          onKeep(link);
          if (why === 'lost' && alive.current) setLostCancel(true);
          onChanged();
        }}
        onLinkGone={linkGone}
      />
    ) : (
      <div>{stopButton(false)}</div>
    )
  ) : null);
  // More than the panel shows is on 1Office's Manage Budgets page, for a person the server
  // offers it to; it opens on this opening's budget, with the opening highlighted. A request
  // still being raised has the link in its own box. The foot is drawn only when it holds
  // something: never an empty grey band.
  // Asked beside the link's own ask (one shared question to the server): the band is drawn only when there is something in it.
  // Not for a deleted opening: there is nothing of it on that page.
  const budgetsOffered = usePmLaunchStatus(projectId, 'manage-budgets', canManage && !sending && !deleted).available;
  const budgetsLink = !sending && !deleted
    ? <ManageBudgetsLink projectId={projectId} active={canManage} look="button" label={OPENING_WORDS.inManageBudgets} budgetId={link.budget?.id} openingId={link.openingId} />
    : null;
  const footShown = !!stopArea || (!!budgetsLink && budgetsOffered);

  return (
    <section aria-label={opening} className={single ? '' : 'rounded-lg border border-gray-200 bg-white'}>
      <div className={single ? 'space-y-3' : 'space-y-3 p-4 pb-0'}>
        {!single && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-[15px] font-semibold text-gray-900">{opening}</h4>
            <OpeningStatusPill link={link} deleted={goneByRead} />
          </div>
        )}

        {error && <FormErrorBanner message={error} />}

        {sending && (
          <RaiseRequestStatus
            projectId={projectId}
            link={link}
            canManage={canManage}
            onResult={raiseAnswered}
            onHiringGone={onHiringGone}
          />
        )}
        {!sending && raiseEnded && (
          <p role="status" className="rounded-lg bg-gray-50 px-3 py-2 text-[13px] text-gray-800">{raiseEnded}</p>
        )}
        {raisedFlag && (
          <p role="note" className={FLAG_LINE} title={raisedFlag.sentence}>
            <span className="font-semibold">{raisedFlag.title}.</span> {raisedFlag.sentence}
          </p>
        )}
        {/* A cancel Recruitment has not confirmed: where it stands. Checked, never sent again. */}
        {cancelOpen && (
          <CancelStatus
            // A box of its own once the link is no longer followed: what the last check said
            // of the followed link is not what stands now, and its checks start afresh.
            key={`${link.id}:${link.state}`}
            projectId={projectId}
            link={link}
            canManage={canManage}
            initial={cancelWaiting}
            onResult={cancelAnswered}
            onHiringGone={onHiringGone}
          />
        )}
        {!cancelOpen && cancelEnded && (
          <p role="status" className="rounded-lg bg-gray-50 px-3 py-2 text-[13px] text-gray-800">{cancelEnded}</p>
        )}
        {readingLost && (
          <p role="status" className="inline-flex items-center gap-2 text-sm text-gray-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {CANCEL_WORDS.lostChecking}
          </p>
        )}
        {/* A move Recruitment has not confirmed: where it stands. Checked, never sent again. */}
        <div ref={moveSaid} className="empty:hidden">
          {moveOpen && (
            <MoveStatus
              projectId={projectId}
              link={link}
              canManage={canManage}
              message={moveWaiting?.message ?? null}
              onResult={moveAnswered}
              onNothingOpen={(sentence) => {
                setMoveWaiting(null);
                setMoveEnded(sentence);
              }}
              onHiringGone={onHiringGone}
            />
          )}
          {!moveOpen && moveEnded && (
            <p role="status" className="rounded-lg bg-gray-50 px-3 py-2 text-[13px] text-gray-800">{moveEnded}</p>
          )}
        </div>

        {/* Deleted in Recruitment: one plain notice in place of its questions, numbers and tabs. */}
        {deleted && <OpeningDeleted link={link} byStatus={goneByStatus} underFoot={single && footShown} />}

        {!deleted && (
          <>
            {/* The questions a message from Recruitment left on this link, each with what can be done. */}
            <HiringAttentionBox
              projectId={projectId}
              link={link}
              canManage={canManage}
              onAnswered={(result) => {
                // The owner is told whether or not this panel is still open.
                onChanged(result.link);
                if (!alive.current) return;
                // A closed link leaves a list of live links: the panel keeps its card.
                if (result.link.state === 'closed') onReleased(result.link);
                setTimelineReads((n) => n + 1);
              }}
              // The link's own stop, with its own question: nothing is stopped by this click.
              onStop={() => {
                setError(null);
                setConfirming(true);
              }}
              onRaiseAgain={onRaiseAgain}
              onOutOfDate={() => {
                onChanged();
                if (alive.current) setTimelineReads((n) => n + 1);
              }}
              onLinkGone={linkGone}
              onHiringGone={onHiringGone}
            />
            {/* Someone joined on a store's own opening: who that may be, and placing them at the store. */}
            <HiringJoinerBox
              projectId={projectId}
              link={link}
              canManage={canManage}
              onPlaced={(result) => {
                // The owner is told whether or not this panel is still open.
                onChanged(result.link);
                if (!alive.current) return;
                // A closed link leaves a list of live links: the panel keeps its card.
                if (result.link.state === 'closed') onReleased(result.link);
                setTimelineReads((n) => n + 1);
              }}
              onOutOfDate={() => {
                onChanged();
                if (alive.current) setTimelineReads((n) => n + 1);
              }}
              onLinkGone={linkGone}
              onHiringGone={onHiringGone}
            />

            <OpeningNumbers link={link} details={details.data} />

            {/* Not beside a question about the same change: that box says it, and asks. */}
            {live && link.flags?.addressChanged && !attentionAsks(link, 'address_changed') && (
              <p role="note" className={FLAG_LINE}>
                <span className="font-semibold">Address changed in Recruitment.</span>{' '}
                {address.sameText ? 'Only the map location moved.' : [address.linked && `Was: ${address.linked}`, address.now && `Now: ${address.now}`].filter(Boolean).join(' · ')}
              </p>
            )}
            {live && link.flags?.positionsChanged && !attentionAsks(link, 'positions_changed') && (
              <p role="note" className={FLAG_LINE}>
                <span className="font-semibold">Positions changed in Recruitment.</span>
                {link.numbers && ` Was ${link.positions} · now ${link.numbers.positions}`}
              </p>
            )}
            {live && link.flags?.joinerExiting && (
              <p role="note" className={FLAG_LINE}><span className="font-semibold">The person who joined is exiting.</span></p>
            )}

            <OpeningTabs
              projectId={projectId}
              link={link}
              details={details.data}
              detailsState={details.state}
              detailsError={details.error}
              onRetryDetails={details.retry}
              historyShown={historyShown}
              onHistory={setHistoryShown}
              history={timeline}
              extra={extraNow}
              onHiringGone={onHiringGone}
              onNotListed={() => setTabsSaidGone(true)}
            />
          </>
        )}
      </div>

      {/* One opening on its own: its foot is the window's foot, pinned to the bottom edge while
          the numbers and tabs scroll between the heading and it (mock-up, 15 Oct 2026). Sticky
          stops at the window body's 16px bottom padding, so a strip of the same grey under it
          (after:) covers that padding: nothing scrolls through below the buttons.
          While "Move to another store" is open the foot is NOT pinned: its form and review can
          be taller than the window's 60vh body, and a pinned box taller than that has its top
          out of reach. It then scrolls with the rest, and the move's window brings itself into view.
          The buttons' area takes the whole row on a phone (Manage Budgets wraps under it), and
          beside Manage Budgets from a laptop's width; the move's window always takes the whole
          row, with Manage Budgets left out meanwhile: it is a form and a review, not a button. */}
      {(stopArea || budgetsLink) && (
        <div
          className={!footShown ? 'hidden' : single
            ? `${moveShown ? 'relative' : 'sticky bottom-0 z-10'} -mx-6 -mb-4 flex flex-wrap items-start justify-between gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3 after:absolute after:inset-x-0 after:top-full after:h-4 after:rounded-b-lg after:bg-gray-50`
            : 'flex flex-wrap items-start justify-between gap-2 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-3'}
        >
          <div className={moveShown ? 'min-w-0 basis-full' : 'min-w-0 grow basis-full sm:basis-0'}>{stopArea}</div>
          {!moveShown && budgetsLink}
        </div>
      )}
    </section>
  );
}

export function HiringPanel({
  projectId, subject, links, canManage, canCancel = false, onClose, onChanged, onLinkHiring, onRaiseHiring, onHiringGone,
  requests = NO_REQUESTS, onOpenRequest, onAskHiring, onRaiseAgain, start, notice, onRaiseReplacing, onAskReplacing,
}: {
  projectId: number;
  subject: HiringPanelSubject;
  /**
   * One short line at the top of the window, from whoever opened it for one step (the question
   * asked when someone is deployed: "<person> is deployed. Now finish moving Opening N.").
   */
  notice?: string;
  /**
   * Opened to do one thing to one opening, chosen outside this window (the question asked after
   * a deploy): that opening's card opens the step by itself, by its own rules. Without it the
   * window opens as it always did.
   */
  start?: HiringPanelStart;
  /** The subject's links from the owner's list, as that list is now. */
  links: readonly HiringLink[];
  canManage: boolean;
  /**
   * The list answer says this person may cancel openings in Recruitment for this project
   * (Cancel is on for it, they hold "Raise & cancel hiring" and they have a 1Office sign-in).
   * Without it nothing of Cancel is offered, and every card's foot is as it always was.
   */
  canCancel?: boolean;
  onClose: () => void;
  /**
   * The owner's list is out of date. With the link as the server just answered it when
   * this panel stopped one, a request to raise hiring moved on, or a cancel was answered;
   * with nothing when someone else had stopped it first, or when what this person may do
   * here has to be read again.
   */
  onChanged: (stopped?: HiringLink) => void;
  /** Offered in the footer to a user who may manage; closes this panel and opens Link hiring. */
  onLinkHiring?: () => void;
  /** Given only when the server says this person may raise hiring here; closes this panel and opens Raise hiring. */
  onRaiseHiring?: () => void;
  /** Hiring was switched off for the project while the panel was open. */
  onHiringGone: () => void;
  /** The subject's hiring requests that are waiting for confirmation, as the owner's list has them now. */
  requests?: readonly HiringRequest[];
  /** Opens the hiring request window on one of them; closes this panel. Without it, the card has no button. */
  onOpenRequest?: (request: HiringRequest) => void;
  /**
   * Given only for a person who may send a hiring request here and may not raise hiring
   * themselves; closes this panel and opens the Raise hiring window in ask mode.
   */
  onAskHiring?: () => void;
  /**
   * For "Raise again" on an opening Recruitment deleted or closed: closes this panel and
   * opens Raise hiring for the same store or role. Where it is not given, `onRaiseHiring`
   * stands in; with neither, "Raise again" is not drawn.
   */
  onRaiseAgain?: () => void;
  /**
   * "Raise again" on an opening deleted in Recruitment (21 Oct 2026): closes this panel and
   * opens Raise hiring for that link's store (or, with no store, the dashboard's Raise window)
   * with the link, which that raise stops following once it has made an opening. Given only
   * where the person may raise hiring here. A promise when the owner reads the store first:
   * see ReplacingOpen. Where it is not given, `onRaiseAgain` or `onRaiseHiring` stands in, and
   * nothing is stopped by itself.
   */
  onRaiseReplacing?: (deleted: HiringLink) => ReplacingOpen;
  /**
   * The same for a person who may only ask: "Ask for hiring" in its place. A hiring request lets
   * go of nothing. Where it is not given, `onAskHiring` stands in.
   */
  onAskReplacing?: (deleted: HiringLink) => ReplacingOpen;
}) {
  const [released, setReleased] = useState<HiringLink[]>([]);
  // Cards whose cancel step asked for the owner's list to be read again: each as it was
  // then. Shown only while the owner's list does not hold the link - a row that is listed
  // is the fresher copy - and never beside the same link once it is among `released`.
  const [kept, setKept] = useState<HiringLink[]>([]);
  // The openings whose own read found them deleted in Recruitment (their status does not say so yet): for the window's pill.
  const [readGone, setReadGone] = useState<ReadonlySet<string>>(() => new Set());
  const releasedIds = new Set(released.map((l) => l.id));
  const listedIds = new Set(links.map((l) => l.id));
  // The owner's links first; then the ones this panel kept; then the stopped ones, each as
  // the stop call returned it or as it was when the server said someone else had ended it.
  const cards = [
    ...links.filter((l) => !releasedIds.has(l.id)),
    ...kept.filter((l) => !listedIds.has(l.id) && !releasedIds.has(l.id)),
    ...released,
  ];
  const waitingRequests = requests.filter((request) => request.standing === 'waiting');

  const title = subject.kind === 'store'
    ? `Hiring for ${openingStoreWords({ storeName: subject.storeName, storeCode: subject.storeCode })}`
    : `Hiring for ${subject.roleLabel}`;
  // One opening and nothing else to decide: the window IS that opening (the dashboard's row).
  const single = cards.length === 1 && waitingRequests.length === 0;
  // Who is leaving this store, before the exit is started. Only where the owner knows which store this is.
  const leaving = subject.kind === 'store' && subject.storeId
    ? <StoreLeavingBox projectId={projectId} storeId={subject.storeId} onHiringGone={onHiringGone} />
    : null;
  const card = (link: HiringLink, extra?: ReactNode) => (
    <HiringLinkCard
      key={link.id}
      projectId={projectId}
      link={link}
      listed={listedIds.has(link.id)}
      canManage={canManage}
      canCancel={canManage && canCancel}
      single={single}
      extra={extra}
      startWith={start && start.linkId === link.id ? start.step : undefined}
      onReleased={(closed) => setReleased((prev) => [...prev.filter((l) => l.id !== closed.id), closed])}
      // The latest copy is kept: after a move it follows the new store.
      onKeep={(held) => setKept((prev) => (prev.some((l) => l.id === held.id) ? prev.map((l) => (l.id === held.id ? held : l)) : [...prev, held]))}
      onChanged={onChanged}
      onHiringGone={onHiringGone}
      // "Raise again" opens the same Raise hiring window the footer's button does, where there is one.
      onRaiseAgain={canManage ? onRaiseAgain ?? onRaiseHiring : undefined}
      // On a deleted opening: the owner's Raise window, handed this link; else the same as above.
      onRaiseReplacing={onRaiseReplacing ? () => onRaiseReplacing(link) : canManage ? onRaiseAgain ?? onRaiseHiring : undefined}
      onAskReplacing={onAskReplacing ? () => onAskReplacing(link) : onAskHiring}
      onReadDeleted={(gone) => setReadGone((was) => {
        if (was.has(link.id) === gone) return was;
        const next = new Set(was);
        if (gone) next.add(link.id);
        else next.delete(link.id);
        return next;
      })}
    />
  );

  // ONE window in both layouts, with the openings at the same place in it: when a hiring
  // request starts waiting under an opening shown on its own (or a second opening arrives),
  // the window becomes the store's list and each opening keeps what it was showing - a
  // sentence just answered, a question half asked.
  const only = single ? cards[0] : null;
  const footer = only ? undefined : (
    <>
      <button
        type="button"
        className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        onClick={onClose}
      >
        {RAISE_WORDS.close}
      </button>
      {/* Link an opening is the outlined one, Raise (or Ask) the filled one: as the dashboard's buttons (panel 1). */}
      {canManage && onLinkHiring && (
        <button
          type="button"
          className="rounded-lg border border-primary-200 bg-white px-3.5 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
          onClick={onLinkHiring}
        >
          {PANEL_WORDS.link}
        </button>
      )}
      {/* Not while a request for this store has not ended: it may have created an opening. */}
      {canManage && onRaiseHiring && !hasRaiseInFlight(cards) && (
        <button
          type="button"
          className="rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          onClick={onRaiseHiring}
        >
          {RAISE_WORDS.panelButton}
        </button>
      )}
      {/* Not while a hiring request here is undecided, or a raise has not ended: no second one is offered. */}
      {onAskHiring && !hasRequestWaiting(waitingRequests) && !hasRaiseInFlight(cards) && (
        <button
          type="button"
          className="rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          onClick={onAskHiring}
        >
          {REQUEST_WORDS.panelButton}
        </button>
      )}
    </>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={only ? openingTitle(only) : title}
      size="xl"
      footer={footer}
      headerContent={only ? (
        <div className="flex items-center justify-between gap-3 rounded-t-lg bg-white px-6 pb-1 pt-5">
          <h3 className="flex flex-wrap items-center gap-2.5 text-lg font-semibold text-gray-900">
            {openingTitle(only)}
            <OpeningStatusPill link={only} deleted={readGone.has(only.id)} />
          </h3>
          <button type="button" aria-label={RAISE_WORDS.close} onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : undefined}
      belowHeader={notice ? <div className={NOTICE_BOX}><p role="status" className={NOTICE}>{notice}</p></div> : undefined}
    >
      {!only && waitingRequests.length > 0 && (
        <div className="mb-4 space-y-4">
          {waitingRequests.map((request) => (
            <HiringRequestCard
              key={request.id}
              request={request}
              onOpen={onOpenRequest ? () => onOpenRequest(request) : undefined}
            />
          ))}
        </div>
      )}
      {/* One opening on its own has who is leaving at the end of its Details. */}
      {!only && leaving}
      {cards.length === 0 ? (
        <div className="space-y-1 py-4 text-sm text-gray-600">
          <p>{subject.kind === 'store' ? PANEL_WORDS.noneStore : PANEL_WORDS.noneRole}</p>
          {/* Only where this panel has a Link button to point at. */}
          {canManage && onLinkHiring && <p>{PANEL_WORDS.linkHint}</p>}
          {canManage && onRaiseHiring && <p>{RAISE_WORDS.panelEmptyHint}</p>}
          {onAskHiring && waitingRequests.length === 0 && <p>{REQUEST_WORDS.panelEmptyHint}</p>}
        </div>
      ) : (
        <div className={only ? undefined : 'space-y-4'}>
          {cards.map((link) => card(link, only ? leaving : undefined))}
        </div>
      )}
    </Modal>
  );
}

export default HiringPanel;
